// Dependencies
import React, { FC, useState, useRef, useEffect, SyntheticEvent } from 'react'

import type * as Square from '@square/web-sdk'

// Internals
import useEvent from 'Hooks/useEvent'

import { useForm } from '../contexts/form'

import styles from './index.module.less'
import type { ApplePayProps } from './types'

/**
 * Renders a Apple Pay button to use in the Square Web Payment SDK, pre-styled
 * to meet Apple Pay's branding guidelines.
 *
 * **Remember** that you need to set `createPaymentRequest()` in `SquareForm` if
 * you going to use this Payment Method
 *
 * @example
 *
 * ```tsx
 * function App() {
 *   return (
 *     <SquareForm {...props}>
 *       <ApplePay />
 *     </SquareForm>
 *   );
 * }
 * ```
 */
const ApplePay: FC<ApplePayProps> = ({ id = 'rswps-apple-pay', ...props }) => {
  const [applePay, setApplePay] = useState<Square.ApplePay | undefined>(() => undefined)
  const { cardTokenizeResponseReceived, createPaymentRequest, payments } = useForm()
  const containerRef = useRef<HTMLDivElement>(null)

  /**
   * Handle the on click of the Apple Pay button click
   *
   * @param e An event which takes place in the DOM.
   * @returns The data be sended to `cardTokenizeResponseReceived()` function, or an error
   */
  const handlePayment = useEvent(async (e: SyntheticEvent) => {
    e.stopPropagation()

    if (!applePay) {
      console.warn('Apple Pay button was clicked, but no Apple Pay instance was found.')

      return
    }

    try {
      const result = await applePay.tokenize()

      if (result.status === 'OK') {
        return cardTokenizeResponseReceived(result)
      }

      let message = `Tokenization failed with status: ${result.status}`
      if (result?.errors) {
        message += ` and errors: ${JSON.stringify(result?.errors)}`

        throw new Error(message)
      }

      console.warn(message)
    } catch (error) {
      console.error(error)
    }
  })

  useEffect(() => {
    if (!createPaymentRequest) {
      throw new Error('`createPaymentRequest()` is required when using digital wallets')
    }

    const abortController = new AbortController()
    const { signal } = abortController

    const start = async (signal: AbortSignal) => {
      const paymentRequest = payments?.paymentRequest(createPaymentRequest)

      if (!paymentRequest) {
        throw new Error('`paymentRequest` is required when using digital wallets')
      }

      try {
        const applePay = await payments?.applePay(paymentRequest).then((res) => {
          if (signal?.aborted) {
            return
          }

          setApplePay(res)

          return res
        })

        if (signal.aborted) {
          await applePay?.destroy()
        }
      } catch (error) {
        console.error('Initializing Apple Pay failed', error)
      }
    }

    void start(signal)

    return () => {
      abortController.abort()
    }
  }, [createPaymentRequest, payments])

  return (
    <div
      className={styles.applePayContainer}
      {...props}
      ref={containerRef}
      id={id}
      style={{
        display: applePay ? 'block' : 'none',
        opacity: applePay ? 1 : 0.5,
        pointerEvents: applePay ? 'auto' : 'none',
        visibility: applePay ? 'visible' : 'hidden',
      }}
      // We need to make this styles to be able to use event listener
      onClick={handlePayment}
    ></div>
  )
}

export default ApplePay
export * from './types'
